import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Search, Upload, Download, Clock, FileText, Package, X, History, Pencil, Layers } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { CostRevisionForm, CostRevisionHistory } from '../components/CostRevision';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtCur = (n) => `₹${fmt(n)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const fmtSize = (b) => { if (!b) return ''; if (b < 1024) return `${b}B`; if (b < 1048576) return `${(b/1024).toFixed(1)}KB`; return `${(b/1048576).toFixed(1)}MB`; };

function AddCostingModal({ supplierId, onClose, onSaved, editData }) {
  const [parts, setParts] = useState([]);
  const [partSearch, setPartSearch] = useState('');
  const [form, setForm] = useState(editData ? {
    part_id: editData.part_id, po_price_per_unit: editData.po_price_per_unit,
    supplier_part_number: editData.supplier_part_number || '', supplier_part_name: editData.supplier_part_name || '',
    po_number: editData.po_number || '', effective_date: editData.effective_date?.slice(0,10) || '',
    notes: editData.notes || '', change_reason: '',
    material_grade: editData.material_grade || '',
    rm_cost: editData.rm_cost ?? '', scrap_recovery: editData.scrap_recovery ?? '',
    plating_cost: editData.plating_cost ?? '', tooling_cost_per_pc: editData.tooling_cost_per_pc ?? '',
    overhead_cost: editData.overhead_cost ?? '',
    processes: editData.processes?.length
      ? editData.processes.map(p => ({ name: p.name || '', cost: p.cost ?? '', tonnage: p.tonnage ?? '', rate_factor: p.rate_factor ?? '', multiplier: p.multiplier ?? '' }))
      : [{ name: '', cost: '', tonnage: '', rate_factor: '', multiplier: '' }],
    bop_items: editData.bop_items?.length ? editData.bop_items : [{ name: '', cost: '' }],
    calc_mode: editData?.calc_mode ?? false,
    thickness_mm: editData?.thickness_mm ?? '', strip_width_mm: editData?.strip_width_mm ?? '',
    strip_pitch_mm: editData?.strip_pitch_mm ?? '', material_density: editData?.material_density ?? '',
    gross_weight_kg: editData?.gross_weight_kg ?? '', net_weight_kg: editData?.net_weight_kg ?? '',
    rm_rate_per_kg: editData?.rm_rate_per_kg ?? '', scrap_rate_per_kg: editData?.scrap_rate_per_kg ?? '',
    tool_maintenance_pct: editData?.tool_maintenance_pct ?? 5, add_on_pct: editData?.add_on_pct ?? 40,
    packaging_freight_pct: editData?.packaging_freight_pct ?? 1, packaging_freight_fixed: editData?.packaging_freight_fixed ?? '',
    profit_pct: editData?.profit_pct ?? 5,
    packaging_freight_cost: editData?.packaging_freight_cost ?? '', profit_cost: editData?.profit_cost ?? '',
  } : {
    part_id: '', po_price_per_unit: '', supplier_part_number: '', supplier_part_name: '',
    po_number: '', effective_date: new Date().toISOString().slice(0,10), notes: '',
    material_grade: '', rm_cost: '', scrap_recovery: '', plating_cost: '',
    tooling_cost_per_pc: '', overhead_cost: '',
    processes: [{ name: '', cost: '', tonnage: '', rate_factor: '', multiplier: '' }],
    bop_items: [{ name: '', cost: '' }],
    calc_mode: false,
    thickness_mm: '', strip_width_mm: '', strip_pitch_mm: '', material_density: '',
    gross_weight_kg: '', net_weight_kg: '', rm_rate_per_kg: '', scrap_rate_per_kg: '',
    tool_maintenance_pct: 5, add_on_pct: 40, packaging_freight_pct: 1, packaging_freight_fixed: '',
    profit_pct: 5, packaging_freight_cost: '', profit_cost: '',
  });
  const [saving, setSaving] = useState(false);
  const [fgPart, setFgPart] = useState(null);
  const isEdit = !!editData;
  const priceChanged = isEdit && form.po_price_per_unit !== '' && Number(form.po_price_per_unit) !== editData.po_price_per_unit;

  const { user } = useAuth();
  // Revision history (Process Route / Plating / Overhead) — queued this session, sent on save.
  const [processRevisions, setProcessRevisions] = useState([]);
  const [platingRevisions, setPlatingRevisions] = useState([]);
  const [overheadRevisions, setOverheadRevisions] = useState([]);
  const [reviseTarget, setReviseTarget] = useState(null); // {kind, idx?, name?, prev}
  const [historyType, setHistoryType] = useState(null);   // 'process' | 'plating' | 'overhead'
  const origProcessNames = new Set((editData?.processes || []).map(p => (p.name || '').trim().toLowerCase()));

  const applyRevision = (rec) => {
    if (reviseTarget?.kind === 'process') {
      setProcess(reviseTarget.idx, 'cost', rec.revised_cost);
      setProcessRevisions(prev => [...prev, rec]);
    } else if (reviseTarget?.kind === 'plating') {
      setForm(f => ({ ...f, plating_cost: rec.revised_cost }));
      setPlatingRevisions(prev => [...prev, { ...rec, process_name: undefined }]);
    } else if (reviseTarget?.kind === 'overhead') {
      setForm(f => ({ ...f, overhead_cost: rec.revised_cost }));
      setOverheadRevisions(prev => [...prev, { ...rec, process_name: undefined }]);
    }
    setReviseTarget(null);
  };

  useEffect(() => {
    if (!isEdit) {
      api.get('/parts?limit=5000').then(r => {
        const d = r.data.parts || r.data;
        setParts(d);
      }).catch(() => {});
    }
  }, [isEdit]);

  useEffect(() => {
    const pid = form.part_id || editData?.part_id;
    if (!pid) return;
    if (fgPart && fgPart.part_id === pid) return;
    api.get(`/parts/${pid}`).then(r => {
      setFgPart(r.data);
      if (!form.material_grade && r.data?.material_grade) {
        setForm(f => ({ ...f, material_grade: r.data.material_grade }));
      }
    }).catch(() => {});
  }, [form.part_id, editData?.part_id]);

  const filteredParts = parts.filter(p =>
    !partSearch || p.part_number?.toLowerCase().includes(partSearch.toLowerCase()) || p.part_name?.toLowerCase().includes(partSearch.toLowerCase())
  ).slice(0, 50);

  const setProcess = (i, k, v) => setForm(f => ({
    ...f, processes: f.processes.map((p, idx) => idx === i ? { ...p, [k]: v } : p)
  }));
  const setProcessField = setProcess;
  const addProcess = () => setForm(f => ({ ...f, processes: [...f.processes, { name: '', cost: '', tonnage: '', rate_factor: '', multiplier: '' }] }));
  const removeProcess = (i) => setForm(f => ({
    ...f, processes: f.processes.length > 1 ? f.processes.filter((_, idx) => idx !== i) : f.processes
  }));
  const setBop = (i, k, v) => setForm(f => ({
    ...f, bop_items: (f.bop_items || []).map((b, idx) => idx === i ? { ...b, [k]: v } : b)
  }));
  const addBop = () => setForm(f => ({ ...f, bop_items: [...(f.bop_items || []), { name: '', cost: '' }] }));
  const removeBop = (i) => setForm(f => ({
    ...f, bop_items: (f.bop_items || []).length > 1 ? f.bop_items.filter((_, idx) => idx !== i) : f.bop_items
  }));

  const sumComponents = (() => {
    const rm = parseFloat(form.rm_cost) || 0;
    const scrap = parseFloat(form.scrap_recovery) || 0;
    const plating = parseFloat(form.plating_cost) || 0;
    const tooling = parseFloat(form.tooling_cost_per_pc) || 0;
    const overhead = parseFloat(form.overhead_cost) || 0;
    const procs = (form.processes || []).reduce((s, p) => s + (parseFloat(p.cost) || 0), 0);
    const bops = (form.bop_items || []).reduce((s, b) => s + (parseFloat(b.cost) || 0), 0);
    const pf = parseFloat(form.packaging_freight_cost) || 0;
    const prof = parseFloat(form.profit_cost) || 0;
    return (rm - scrap) + procs + plating + bops + tooling + overhead + pf + prof;
  })();
  const poNum = parseFloat(form.po_price_per_unit) || 0;
  const unaccounted = poNum - sumComponents;
  const hasComponents = sumComponents !== 0;

  // Live derivation for calc_mode
  const derived = (() => {
    if (!form.calc_mode) return null;
    const t = parseFloat(form.thickness_mm) || 0;
    const w = parseFloat(form.strip_width_mm) || 0;
    const p = parseFloat(form.strip_pitch_mm) || 0;
    const d = parseFloat(form.material_density) || 0;
    const manualGross = parseFloat(form.gross_weight_kg) || 0;
    const gross = (t && w && p && d) ? (t * w * p * d) / 1_000_000 : manualGross;
    const net = parseFloat(form.net_weight_kg) || 0;
    const rmRate = parseFloat(form.rm_rate_per_kg) || 0;
    const scrapRate = parseFloat(form.scrap_rate_per_kg) || 0;
    const rm_cost = gross * rmRate;
    const scrap_recovery = Math.max(gross - net, 0) * scrapRate;
    const procs_with_cost = (form.processes || []).map(pr => {
      const ton = parseFloat(pr.tonnage);
      if (!isNaN(ton) && ton > 0) {
        const rate = parseFloat(pr.rate_factor) || 0.0055;
        const mult = parseFloat(pr.multiplier) || 1.0;
        return { ...pr, cost: (ton * rate * mult).toFixed(4) };
      }
      return pr;
    });
    const process_total = procs_with_cost.reduce((s, pr) => s + (parseFloat(pr.cost) || 0), 0);
    const toolPct = parseFloat(form.tool_maintenance_pct) || 0;
    const addOnPct = parseFloat(form.add_on_pct) || 0;
    const tooling_cost_per_pc = process_total * toolPct / 100;
    const overhead_cost = process_total * addOnPct / 100;
    const rmPlusProcess = (rm_cost - scrap_recovery) + process_total;
    const pfFixed = parseFloat(form.packaging_freight_fixed);
    const pfPct = parseFloat(form.packaging_freight_pct) || 0;
    const packaging_freight_cost = (!isNaN(pfFixed) && pfFixed > 0) ? pfFixed : (rmPlusProcess * pfPct / 100);
    const profitPct = parseFloat(form.profit_pct) || 0;
    const profit_cost = rmPlusProcess * profitPct / 100;
    return { gross, net, rm_cost, scrap_recovery, procs_with_cost, process_total,
             tooling_cost_per_pc, overhead_cost, packaging_freight_cost, profit_cost };
  })();

  const handleSave = async () => {
    if (!isEdit && !form.part_id) { toast.error('Select a part'); return; }
    if (!form.po_price_per_unit || Number(form.po_price_per_unit) <= 0) { toast.error('PO price is required'); return; }
    if (isEdit && priceChanged && !form.change_reason?.trim()) { toast.error('Change reason required when updating price'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {};
        if (priceChanged) { payload.po_price_per_unit = Number(form.po_price_per_unit); payload.change_reason = form.change_reason; }
        if (form.supplier_part_number !== (editData.supplier_part_number || '')) payload.supplier_part_number = form.supplier_part_number;
        if (form.supplier_part_name !== (editData.supplier_part_name || '')) payload.supplier_part_name = form.supplier_part_name;
        if (form.po_number !== (editData.po_number || '')) payload.po_number = form.po_number;
        if (form.effective_date !== (editData.effective_date?.slice(0,10) || '')) payload.effective_date = form.effective_date;
        if (form.notes !== (editData.notes || '')) payload.notes = form.notes;
        const breakdownFields = ['material_grade', 'rm_cost', 'scrap_recovery', 'plating_cost', 'tooling_cost_per_pc', 'overhead_cost'];
        for (const bf of breakdownFields) {
          const newVal = bf === 'material_grade' ? (form[bf] || null) : (Number(form[bf]) || 0);
          const oldVal = bf === 'material_grade' ? (editData[bf] || null) : (Number(editData[bf]) || 0);
          if (newVal !== oldVal) payload[bf] = newVal;
        }
        const newProcs = (form.processes || []).filter(p => p.name && p.name.trim()).map(p => ({
          name: p.name, cost: Number(p.cost) || 0,
          tonnage: p.tonnage !== '' && p.tonnage != null ? parseFloat(p.tonnage) : null,
          rate_factor: p.rate_factor !== '' && p.rate_factor != null ? parseFloat(p.rate_factor) : null,
          multiplier: p.multiplier !== '' && p.multiplier != null ? parseFloat(p.multiplier) : null,
        }));
        const oldProcs = (editData.processes || []);
        if (JSON.stringify(newProcs) !== JSON.stringify(oldProcs)) payload.processes = newProcs;
        const newBops = (form.bop_items || []).filter(b => b.name && b.name.trim()).map(b => ({ name: b.name, cost: Number(b.cost) || 0 }));
        const oldBops = (editData.bop_items || []);
        if (JSON.stringify(newBops) !== JSON.stringify(oldBops)) payload.bop_items = newBops;
        // Calculator fields
        const calcFields = ['calc_mode', 'thickness_mm', 'strip_width_mm', 'strip_pitch_mm',
          'material_density', 'gross_weight_kg', 'net_weight_kg', 'rm_rate_per_kg', 'scrap_rate_per_kg',
          'tool_maintenance_pct', 'add_on_pct', 'packaging_freight_pct', 'packaging_freight_fixed',
          'profit_pct', 'packaging_freight_cost', 'profit_cost'];
        for (const cf of calcFields) {
          const newV = cf === 'calc_mode' ? !!form[cf] : (parseFloat(form[cf]) || (cf.endsWith('_cost') ? 0 : null));
          const oldV = cf === 'calc_mode' ? !!editData[cf] : (parseFloat(editData[cf]) || (cf.endsWith('_cost') ? 0 : null));
          if (JSON.stringify(newV) !== JSON.stringify(oldV)) payload[cf] = newV;
        }
        if (processRevisions.length) payload.process_revisions = processRevisions;
        if (platingRevisions.length) payload.plating_revisions = platingRevisions;
        if (overheadRevisions.length) payload.overhead_revisions = overheadRevisions;
        await api.put(`/supplier-costings/${editData.costing_id}`, payload);
        toast.success('Costing updated');
      } else {
        await api.post('/supplier-costings', {
          supplier_id: supplierId, part_id: form.part_id,
          po_price_per_unit: Number(form.po_price_per_unit),
          supplier_part_number: form.supplier_part_number || null,
          supplier_part_name: form.supplier_part_name || null,
          po_number: form.po_number || null, effective_date: form.effective_date || null,
          notes: form.notes || null, material_grade: form.material_grade || null,
          rm_cost: Number(form.rm_cost) || 0, scrap_recovery: Number(form.scrap_recovery) || 0,
          plating_cost: Number(form.plating_cost) || 0, tooling_cost_per_pc: Number(form.tooling_cost_per_pc) || 0,
          overhead_cost: Number(form.overhead_cost) || 0,
          processes: (form.processes || []).filter(p => p.name && p.name.trim()).map(p => ({
            name: p.name, cost: parseFloat(p.cost) || 0,
            tonnage: p.tonnage !== '' && p.tonnage != null ? parseFloat(p.tonnage) : null,
            rate_factor: p.rate_factor !== '' && p.rate_factor != null ? parseFloat(p.rate_factor) : null,
            multiplier: p.multiplier !== '' && p.multiplier != null ? parseFloat(p.multiplier) : null,
          })),
          bop_items: (form.bop_items || []).filter(b => b.name && b.name.trim()),
          calc_mode: !!form.calc_mode,
          thickness_mm: parseFloat(form.thickness_mm) || null,
          strip_width_mm: parseFloat(form.strip_width_mm) || null,
          strip_pitch_mm: parseFloat(form.strip_pitch_mm) || null,
          material_density: parseFloat(form.material_density) || null,
          gross_weight_kg: parseFloat(form.gross_weight_kg) || null,
          net_weight_kg: parseFloat(form.net_weight_kg) || null,
          rm_rate_per_kg: parseFloat(form.rm_rate_per_kg) || null,
          scrap_rate_per_kg: parseFloat(form.scrap_rate_per_kg) || null,
          tool_maintenance_pct: parseFloat(form.tool_maintenance_pct) || null,
          add_on_pct: parseFloat(form.add_on_pct) || null,
          packaging_freight_pct: parseFloat(form.packaging_freight_pct) || null,
          packaging_freight_fixed: parseFloat(form.packaging_freight_fixed) || null,
          profit_pct: parseFloat(form.profit_pct) || null,
          packaging_freight_cost: parseFloat(form.packaging_freight_cost) || 0,
          profit_cost: parseFloat(form.profit_cost) || 0,
        });
        toast.success('Costing created');
      }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit' : 'Add'} Part Costing</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          {!isEdit && (
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Our Part *</label>
              <input type="text" placeholder="Search part number or name..." value={partSearch} onChange={e => setPartSearch(e.target.value)}
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm mb-1 focus:outline-none focus:ring-1 focus:ring-slate-900" />
              {partSearch && (
                <div className="border border-slate-200 rounded-sm max-h-32 overflow-y-auto bg-white">
                  {filteredParts.map(p => (
                    <div key={p.part_id} onClick={() => { setForm(f => ({ ...f, part_id: p.part_id })); setPartSearch(`${p.part_number} — ${p.part_name}`); }}
                      className={`px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 ${form.part_id === p.part_id ? 'bg-slate-100 font-semibold' : ''}`}>
                      <span className="font-mono">{p.part_number}</span> — {p.part_name} <span className="text-slate-400">({p.customer_name})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isEdit && <p className="text-sm text-slate-600">Part: <span className="font-mono font-semibold">{editData.part_number}</span> — {editData.part_name}</p>}

          {fgPart && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs">
              <p className="text-[10px] uppercase font-bold text-slate-500 mb-1">FG Part Reference (read-only)</p>
              <div className="grid grid-cols-3 gap-2 text-slate-700">
                <div>RM Grade: <span className="font-semibold">{fgPart.material_grade || '—'}</span></div>
                <div>Net Wt: <span className="font-mono font-semibold">{fgPart.net_weight ?? '—'} kg</span></div>
                <div>Gross Wt: <span className="font-mono font-semibold">{fgPart.gross_weight ?? '—'} kg</span></div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Supplier's Part Number", key: 'supplier_part_number', placeholder: "Supplier's code" },
              { label: "Supplier's Part Name", key: 'supplier_part_name', placeholder: "Supplier's description" },
              { label: 'PO Number', key: 'po_number', placeholder: 'PO-001' },
              { label: 'Effective Date', key: 'effective_date', type: 'date' },
            ].map(({ label, key, placeholder, type }) => (
              <div key={key}>
                <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">{label}</label>
                <input type={type || 'text'} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                  className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900" />
              </div>
            ))}
          </div>

          <div>
            <div className="flex items-end justify-between mb-1">
              <label className="text-xs font-bold uppercase text-slate-500">PO Price per Unit *</label>
              {hasComponents && (
                <p className="text-[11px] font-mono text-slate-500">
                  Sum: <span className="font-bold text-slate-700">{fmtCur(sumComponents)}</span>
                  {poNum > 0 && Math.abs(unaccounted) >= 0.01 && (
                    <span className={unaccounted > 0 ? 'text-amber-600 ml-2' : 'text-red-600 ml-2'}>
                      ({unaccounted > 0 ? '+' : ''}{unaccounted.toFixed(2)} unaccounted)
                    </span>
                  )}
                  {poNum > 0 && Math.abs(unaccounted) < 0.01 && (
                    <span className="text-emerald-600 ml-2">matches</span>
                  )}
                </p>
              )}
            </div>
            <input type="number" step="0.01" value={form.po_price_per_unit} onChange={e => setForm(f => ({ ...f, po_price_per_unit: e.target.value }))}
              placeholder="0.00" data-testid="po-price-input"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-slate-900" />
          </div>

          {isEdit && priceChanged && (
            <div>
              <label className="text-xs font-bold uppercase text-red-500 mb-1 block">Change Reason *</label>
              <input type="text" value={form.change_reason} onChange={e => setForm(f => ({ ...f, change_reason: e.target.value }))} placeholder="Why is the price changing?"
                className="w-full border border-red-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-red-500" />
            </div>
          )}

          <div className="border-t border-slate-200 pt-3">
            <p className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-2">Cost Breakdown (per piece)</p>

            {/* Calculator toggle */}
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 mb-3">
              <div>
                <p className="text-xs font-bold text-slate-700">Use Calculator (Excel-style derivation)</p>
                <p className="text-[10px] text-slate-500">Enter strip dimensions + rates + tonnage. Costs auto-derive.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={!!form.calc_mode}
                  onChange={e => setForm(f => ({ ...f, calc_mode: e.target.checked }))}
                  data-testid="calc-mode-toggle" className="sr-only peer" />
                <div className="w-9 h-5 bg-slate-300 peer-checked:bg-orange-500 rounded-full peer transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
              </label>
            </div>

            {form.calc_mode && (
              <div className="border border-orange-200 bg-orange-50/30 rounded-sm p-3 mb-3">
                <p className="text-[11px] uppercase font-bold text-orange-700 mb-2 tracking-wider">Calculator Inputs</p>
                <p className="text-[10px] font-semibold text-slate-600 mt-1 mb-1">Strip Dimensions (for gross weight)</p>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'Thickness (mm)', key: 'thickness_mm', placeholder: '1.6' },
                    { label: 'Width (mm)', key: 'strip_width_mm', placeholder: '130' },
                    { label: 'Pitch (mm)', key: 'strip_pitch_mm', placeholder: '80.5' },
                    { label: 'Density', key: 'material_density', placeholder: '7.85' },
                  ].map(({ label, key, placeholder }) => (
                    <div key={key}>
                      <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">{label}</label>
                      <input type="number" step="any" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        placeholder={placeholder} data-testid={`calc-${key}`}
                        className="w-full border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-orange-500" />
                    </div>
                  ))}
                </div>
                {derived && <p className="text-[10px] text-slate-600 mt-1">Derived gross: <span className="font-mono font-bold">{derived.gross.toFixed(4)} kg</span></p>}
                <p className="text-[10px] font-semibold text-slate-600 mt-3 mb-1">Weights (supplier-specific)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">Gross Wt (kg) override</label>
                    <input type="number" step="any" value={form.gross_weight_kg} onChange={e => setForm(f => ({ ...f, gross_weight_kg: e.target.value }))}
                      placeholder="(auto from dims)" className="w-full border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">Net Wt (kg) *</label>
                    <input type="number" step="any" value={form.net_weight_kg} onChange={e => setForm(f => ({ ...f, net_weight_kg: e.target.value }))}
                      placeholder="0.050" data-testid="calc-net-weight" className="w-full border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono" />
                  </div>
                </div>
                <p className="text-[10px] font-semibold text-slate-600 mt-3 mb-1">Rates per kg</p>
                <div className="grid grid-cols-2 gap-2">
                  {[{ label: 'RM Rate (/kg)', key: 'rm_rate_per_kg', placeholder: '60' }, { label: 'Scrap Rate (/kg)', key: 'scrap_rate_per_kg', placeholder: '34' }].map(({ label, key, placeholder }) => (
                    <div key={key}>
                      <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">{label}</label>
                      <input type="number" step="any" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        placeholder={placeholder} className="w-full border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono" />
                    </div>
                  ))}
                </div>
                {derived && <p className="text-[10px] text-slate-600 mt-1">RM: <span className="font-mono font-bold">{fmtCur(derived.rm_cost)}</span>, Scrap: <span className="font-mono font-bold">{fmtCur(derived.scrap_recovery)}</span>, Net RMC: <span className="font-mono font-bold">{fmtCur(derived.rm_cost - derived.scrap_recovery)}</span></p>}
                <p className="text-[10px] font-semibold text-slate-600 mt-3 mb-1">% Markups</p>
                <div className="grid grid-cols-4 gap-2">
                  {[{ label: 'Tool Maint %', key: 'tool_maintenance_pct' }, { label: 'Add-On %', key: 'add_on_pct' }, { label: 'Pkg/Freight %', key: 'packaging_freight_pct' }, { label: 'Profit %', key: 'profit_pct' }].map(({ label, key }) => (
                    <div key={key}>
                      <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">{label}</label>
                      <input type="number" step="any" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        className="w-full border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono" />
                    </div>
                  ))}
                </div>
                <div className="mt-2">
                  <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">Pkg/Freight Fixed — alternative to %</label>
                  <input type="number" step="any" value={form.packaging_freight_fixed} onChange={e => setForm(f => ({ ...f, packaging_freight_fixed: e.target.value }))}
                    placeholder="Leave blank to use %" className="w-full border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono" />
                </div>
              </div>
            )}

            <div className="mb-3">
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">
                Material Grade <span className="text-[10px] text-slate-400 normal-case font-normal">(auto-inherited, editable)</span>
              </label>
              <input type="text" value={form.material_grade} onChange={e => setForm(f => ({ ...f, material_grade: e.target.value }))} placeholder="e.g. CRCA"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'RM Cost (/pc)', key: 'rm_cost' },
                { label: 'Scrap Recovery (/pc)', key: 'scrap_recovery' },
                { label: 'Plating Cost (/pc)', key: 'plating_cost' },
                { label: 'Tooling Cost (/pc)', key: 'tooling_cost_per_pc' },
                { label: 'Overhead (/pc)', key: 'overhead_cost' },
              ].map(({ label, key }) => {
                const kind = key === 'plating_cost' ? 'plating' : key === 'overhead_cost' ? 'overhead' : null;
                const useRevise = isEdit && !form.calc_mode && kind;
                const derivedVal = form.calc_mode && derived
                  ? (key === 'rm_cost' ? derived.rm_cost.toFixed(2)
                     : key === 'scrap_recovery' ? derived.scrap_recovery.toFixed(2)
                     : key === 'tooling_cost_per_pc' ? derived.tooling_cost_per_pc.toFixed(2)
                     : key === 'overhead_cost' ? derived.overhead_cost.toFixed(2)
                     : form[key])
                  : form[key];
                return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1 h-4">
                    <label className="text-xs font-bold uppercase text-slate-500">{label}</label>
                    {kind && isEdit && (
                      <button type="button" onClick={() => setHistoryType(kind)} data-testid={`${kind}-history-btn`}
                        className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-0.5"><History size={10} /> History</button>
                    )}
                  </div>
                  {useRevise ? (
                    <div className="flex gap-2">
                      <div className="flex-1 border border-slate-200 bg-slate-100 rounded-sm h-9 px-3 text-sm font-mono flex items-center text-slate-600" data-testid={`breakdown-${key}`}>{fmtCur(Number(form[key]) || 0)}</div>
                      <button type="button" title="Revise" onClick={() => setReviseTarget({ kind, prev: form[key] })} data-testid={`revise-${kind}`}
                        className="flex items-center gap-1 px-2.5 h-9 border border-slate-300 rounded-sm text-slate-700 text-xs font-semibold hover:bg-slate-50"><Pencil size={13} /> Revise</button>
                    </div>
                  ) : (
                    <input type="number" step="0.01" value={derivedVal}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder="0.00"
                      disabled={form.calc_mode && key !== 'plating_cost'}
                      data-testid={`breakdown-${key}`}
                      className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-slate-900 disabled:bg-slate-100" />
                  )}
                </div>
                );
              })}
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold uppercase text-slate-500">Process Route</label>
                <div className="flex items-center gap-3">
                  {isEdit && (
                    <button type="button" onClick={() => setHistoryType('process')} data-testid="process-history-btn"
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"><History size={12} /> Process Route History</button>
                  )}
                  <button type="button" onClick={addProcess} className="text-xs font-semibold text-orange-500 hover:text-orange-600 flex items-center gap-1" data-testid="supplier-add-process-btn"><Plus size={12} /> Add</button>
                </div>
              </div>
              <div className="space-y-2">
                {form.processes.map((proc, i) => (
                  <div key={i} className={form.calc_mode ? "grid grid-cols-12 gap-1 items-center" : "flex gap-2"}>
                    <input type="text" value={proc.name} onChange={e => setProcessField(i, 'name', e.target.value)} placeholder="Process name"
                      className={form.calc_mode ? "col-span-3 border border-slate-300 rounded-sm h-8 px-2 text-xs" : "flex-1 border border-slate-300 rounded-sm h-8 px-2 text-sm focus:outline-none"} />
                    {form.calc_mode && (
                      <>
                        <input type="number" step="any" value={proc.tonnage} onChange={e => setProcessField(i, 'tonnage', e.target.value)}
                          placeholder="Ton" className="col-span-2 border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono" />
                        <input type="number" step="any" value={proc.rate_factor} onChange={e => setProcessField(i, 'rate_factor', e.target.value)}
                          placeholder="0.0055" className="col-span-2 border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono" />
                        <input type="number" step="any" value={proc.multiplier} onChange={e => setProcessField(i, 'multiplier', e.target.value)}
                          placeholder="x1.0" className="col-span-2 border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono" />
                      </>
                    )}
                    {(isEdit && !form.calc_mode && origProcessNames.has((proc.name || '').trim().toLowerCase())) ? (
                      <>
                        <div className="w-24 border border-slate-200 bg-slate-100 rounded-sm h-8 px-2 text-sm font-mono flex items-center justify-end text-slate-600">{fmtCur(Number(proc.cost) || 0)}</div>
                        <button type="button" title="Revise cost" data-testid={`revise-process-${i}`}
                          onClick={() => setReviseTarget({ kind: 'process', idx: i, name: proc.name, prev: proc.cost })}
                          className="text-slate-400 hover:text-slate-800"><Pencil size={14} /></button>
                      </>
                    ) : (
                      <input type="number" step="0.01" value={proc.cost} onChange={e => setProcessField(i, 'cost', e.target.value)} placeholder="/pc"
                        disabled={form.calc_mode && proc.tonnage !== '' && proc.tonnage != null && proc.tonnage !== 0}
                        className={form.calc_mode ? "col-span-2 border border-slate-300 rounded-sm h-8 px-2 text-xs font-mono disabled:bg-slate-100" : "w-24 border border-slate-300 rounded-sm h-8 px-2 text-sm font-mono focus:outline-none"} />
                    )}
                    {form.processes.length > 1 && <button type="button" onClick={() => removeProcess(i)}
                      className={form.calc_mode ? "col-span-1 text-slate-400 hover:text-red-500" : "text-slate-400 hover:text-red-500"}><X size={14} /></button>}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold uppercase text-slate-500">BOP</label>
                <button type="button" onClick={addBop} className="text-xs font-semibold text-orange-500 hover:text-orange-600 flex items-center gap-1" data-testid="supplier-add-bop-btn"><Plus size={12} /> Add</button>
              </div>
              <div className="space-y-2">
                {(form.bop_items || []).map((bop, i) => (
                  <div key={i} className="flex gap-2">
                    <input type="text" value={bop.name} onChange={e => setBop(i, 'name', e.target.value)} placeholder="BOP name"
                      className="flex-1 border border-slate-300 rounded-sm h-8 px-2 text-sm focus:outline-none" />
                    <input type="number" step="0.01" value={bop.cost} onChange={e => setBop(i, 'cost', e.target.value)} placeholder="/pc"
                      className="w-24 border border-slate-300 rounded-sm h-8 px-2 text-sm font-mono focus:outline-none" />
                    {(form.bop_items || []).length > 1 && <button type="button" onClick={() => removeBop(i)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Packaging/Freight + Profit */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Packaging / Freight (/pc)</label>
              <input type="number" step="0.01"
                value={form.calc_mode && derived ? derived.packaging_freight_cost.toFixed(2) : form.packaging_freight_cost}
                onChange={e => setForm(f => ({ ...f, packaging_freight_cost: e.target.value }))}
                disabled={form.calc_mode} data-testid="breakdown-packaging-freight"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none disabled:bg-slate-100" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Profit (/pc)</label>
              <input type="number" step="0.01"
                value={form.calc_mode && derived ? derived.profit_cost.toFixed(2) : form.profit_cost}
                onChange={e => setForm(f => ({ ...f, profit_cost: e.target.value }))}
                disabled={form.calc_mode} data-testid="breakdown-profit"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none disabled:bg-slate-100" />
            </div>
          </div>

          {/* Live Costing Breakdown — updates automatically as any value is edited */}
          {(() => {
            const D = form.calc_mode && derived ? derived : null;
            const netRm = D ? (D.rm_cost - D.scrap_recovery) : ((parseFloat(form.rm_cost) || 0) - (parseFloat(form.scrap_recovery) || 0));
            const proc = D ? D.process_total : (form.processes || []).reduce((s, p) => s + (parseFloat(p.cost) || 0), 0);
            const plating = parseFloat(form.plating_cost) || 0;
            const bop = (form.bop_items || []).reduce((s, b) => s + (parseFloat(b.cost) || 0), 0);
            const tooling = D ? D.tooling_cost_per_pc : (parseFloat(form.tooling_cost_per_pc) || 0);
            const overhead = D ? D.overhead_cost : (parseFloat(form.overhead_cost) || 0);
            const pkg = D ? D.packaging_freight_cost : (parseFloat(form.packaging_freight_cost) || 0);
            const profit = D ? D.profit_cost : (parseFloat(form.profit_cost) || 0);
            const sum = netRm + proc + plating + bop + tooling + overhead + pkg + profit;
            const po = parseFloat(form.po_price_per_unit) || 0;
            const gap = po - sum;
            const Row = ({ label, val, color, bold, sub }) => (
              <div className={`flex items-center justify-between px-4 py-2 ${bold ? 'bg-slate-50' : ''}`}>
                <p className={`text-sm ${bold ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>{label}{sub && <span className="text-[10px] text-slate-400 font-normal ml-1">{sub}</span>}</p>
                <p className={`font-mono text-sm ${bold ? 'font-black' : 'font-bold'} ${color || 'text-slate-900'}`}>{fmtCur(val)}</p>
              </div>
            );
            return (
              <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="supplier-live-breakdown">
                <div className="bg-slate-800 px-4 py-2"><p className="text-[10px] uppercase font-bold text-slate-300 tracking-wider">Live Costing Breakdown</p></div>
                <div className="divide-y divide-slate-100">
                  <Row label="Net RM Cost" sub="(RM − Scrap)" val={netRm} />
                  {proc > 0 && <Row label="Process Cost" val={proc} />}
                  {plating > 0 && <Row label="Plating Cost" val={plating} color="text-purple-600" />}
                  {bop > 0 && <Row label="BOP Cost" val={bop} color="text-indigo-600" />}
                  {tooling > 0 && <Row label="Tooling / pc" val={tooling} />}
                  {overhead > 0 && <Row label="Overheads" val={overhead} />}
                  {pkg > 0 && <Row label="Packaging / Freight" val={pkg} />}
                  {profit > 0 && <Row label="Profit" val={profit} color="text-emerald-600" />}
                  <Row label="Sum of Components" val={sum} bold />
                  <div className="flex items-center justify-between px-4 py-2 border-t-2 border-slate-200">
                    <p className="text-sm font-semibold text-slate-700">PO Price per Unit</p>
                    <p className="font-mono text-sm font-bold text-slate-900">{fmtCur(po)}</p>
                  </div>
                  <div className={`flex items-center justify-between px-4 py-3 ${Math.abs(gap) < 0.01 ? 'bg-emerald-50' : gap < 0 ? 'bg-red-50' : 'bg-amber-50'}`}>
                    <div>
                      <p className="font-bold text-slate-900">{Math.abs(gap) < 0.01 ? 'Reconciled' : gap < 0 ? 'Over PO price' : 'Unaccounted'}</p>
                      <p className="text-[10px] text-slate-500">PO price − Sum of components</p>
                    </div>
                    <p className={`font-mono font-black text-lg ${Math.abs(gap) < 0.01 ? 'text-emerald-700' : gap < 0 ? 'text-red-700' : 'text-amber-700'}`}>{fmtCur(gap)}</p>
                  </div>
                </div>
              </div>
            );
          })()}

          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-costing-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Add Costing')}
            </button>
          </div>
        </div>
      </DialogContent>

      {reviseTarget && (
        <CostRevisionForm
          title={reviseTarget.kind === 'plating' ? 'Revise Plating Cost'
            : reviseTarget.kind === 'overhead' ? 'Revise Overheads' : 'Revise Process Cost'}
          processName={reviseTarget.kind === 'process' ? reviseTarget.name : undefined}
          previousCost={reviseTarget.prev}
          defaultBy={user?.name || ''}
          onSubmit={applyRevision}
          onClose={() => setReviseTarget(null)} />
      )}
      {historyType === 'process' && (
        <CostRevisionHistory title="Process Route — Revision History" showName
          records={[...(editData?.process_cost_history || []), ...processRevisions]}
          onClose={() => setHistoryType(null)} />
      )}
      {historyType === 'plating' && (
        <CostRevisionHistory title="Plating Cost — Revision History"
          records={[...(editData?.plating_cost_history || []), ...platingRevisions]}
          onClose={() => setHistoryType(null)} />
      )}
      {historyType === 'overhead' && (
        <CostRevisionHistory title="Overheads — Revision History"
          records={[...(editData?.overhead_cost_history || []), ...overheadRevisions]}
          onClose={() => setHistoryType(null)} />
      )}
    </Dialog>
  );
}

function BreakdownRow({ label, value, color = 'slate', bold = false, bg = false }) {
  const fmtRs = (n) => `₹${(Number(n) || 0).toFixed(2)}`;
  const colorClass = color === 'emerald' ? 'text-emerald-600' : 'text-slate-900';
  return (
    <div className={`flex items-center justify-between px-3 py-2 ${bg ? 'bg-slate-100 rounded-sm' : ''}`}>
      <p className={`text-sm ${bold ? 'font-bold' : 'font-semibold'} text-slate-700`}>{label}</p>
      <p className={`font-mono text-sm ${bold ? 'font-black' : 'font-bold'} ${colorClass}`}>{fmtRs(value)}</p>
    </div>
  );
}

function SupplierCostBreakdownModal({ costing, onClose }) {
  if (!costing) return null;
  const b = costing.breakdown || {};
  const fmtRs = (n) => `₹${(Number(n) || 0).toFixed(2)}`;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-slate-900">Supplier Cost Breakdown</DialogTitle></DialogHeader>
        <div className="mt-2 space-y-2" data-testid="supplier-breakdown-body">
          <div className="text-xs text-slate-500 mb-2">
            Part: <span className="font-mono font-semibold">{costing.part_number || costing.part_id}</span>
            {costing.supplier_part_number && <span className="ml-2">| Supplier: <span className="font-mono">{costing.supplier_part_number}</span></span>}
          </div>
          {!b.has_breakdown && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-800">
              No breakdown entered yet. Click Edit to add component costs.
            </div>
          )}
          {b.has_breakdown && (
            <>
              <BreakdownRow label="RM Cost" value={costing.rm_cost} />
              <BreakdownRow label="Less: Scrap Recovery" value={-(costing.scrap_recovery || 0)} color="emerald" />
              <BreakdownRow label="Net RM Cost" value={b.net_rm_cost} bold bg />
              <div className="py-1">
                <BreakdownRow label="Process Cost (total)" value={b.processes_total} />
                {(costing.processes || []).filter(p => p.cost > 0).map((p, i) => (
                  <div key={i} className="flex items-center justify-between pl-4 py-0.5">
                    <p className="text-xs text-slate-500">{p.name}</p>
                    <p className="font-mono text-xs font-semibold text-slate-600">{fmtRs(p.cost)}</p>
                  </div>
                ))}
              </div>
              <BreakdownRow label="Plating Cost" value={costing.plating_cost} />
              <div className="py-1">
                <BreakdownRow label="BOP Cost (total)" value={b.bop_total} />
                {(costing.bop_items || []).filter(x => x.cost > 0).map((x, i) => (
                  <div key={i} className="flex items-center justify-between pl-4 py-0.5">
                    <p className="text-xs text-slate-500">{x.name}</p>
                    <p className="font-mono text-xs font-semibold text-slate-600">{fmtRs(x.cost)}</p>
                  </div>
                ))}
              </div>
              <BreakdownRow label="Tooling Cost (per pc)" value={costing.tooling_cost_per_pc} />
              <BreakdownRow label="Overhead" value={costing.overhead_cost} />
              <BreakdownRow label="Packaging / Freight" value={costing.packaging_freight_cost} />
              <BreakdownRow label="Profit" value={costing.profit_cost} />
              <div className="border-t border-slate-300 mt-2 pt-2">
                <BreakdownRow label="Sum of Components" value={b.sum_components} bold />
                <BreakdownRow label="PO Price per Unit" value={b.po_price} bold />
                {Math.abs(b.unaccounted) >= 0.01 && (
                  <div className="flex items-center justify-between px-3 py-2 mt-1 bg-amber-50 rounded-sm">
                    <p className="text-xs font-semibold text-amber-700">Unaccounted (PO - Sum)</p>
                    <p className="font-mono text-xs font-bold text-amber-700">{fmtRs(b.unaccounted)}</p>
                  </div>
                )}
                {Math.abs(b.unaccounted) < 0.01 && (
                  <div className="text-center text-xs text-emerald-600 font-semibold mt-1">Breakdown reconciles to PO price</div>
                )}
              </div>
            </>
          )}
          <button onClick={onClose} className="w-full mt-3 h-9 border border-slate-300 rounded-sm text-sm font-semibold hover:bg-slate-50">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [costings, setCostings] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('costings');
  const [showAdd, setShowAdd] = useState(false);
  const [editCosting, setEditCosting] = useState(null);
  const [search, setSearch] = useState('');
  const [lookup, setLookup] = useState('');
  const [lookupResults, setLookupResults] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewBreakdown, setViewBreakdown] = useState(null);
  const fileRef = useRef(null);

  const fetchData = async () => {
    try {
      const [supRes, costRes, docRes] = await Promise.all([
        api.get(`/suppliers/${id}`),
        api.get(`/supplier-costings?supplier_id=${id}`),
        api.get(`/suppliers/${id}/documents`),
      ]);
      setSupplier(supRes.data);
      setCostings(costRes.data);
      setDocs(docRes.data);
    } catch { toast.error('Failed to load supplier'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleLookup = async () => {
    if (!lookup.trim()) return;
    try {
      const res = await api.get(`/supplier-costings/lookup?supplier_part_number=${encodeURIComponent(lookup)}&supplier_id=${id}`);
      setLookupResults(res.data);
    } catch { toast.error('Lookup failed'); }
  };

  const handleDeleteCosting = async (c) => {
    if (!window.confirm(`Deactivate costing for ${c.part_number}?`)) return;
    try {
      await api.delete(`/supplier-costings/${c.costing_id}`);
      toast.success('Costing deactivated');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleUploadDoc = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', file.name);
    try {
      await api.post(`/suppliers/${id}/documents`, fd);
      toast.success('Document uploaded');
      fetchData();
      fileRef.current.value = '';
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"?`)) return;
    try {
      await api.delete(`/supplier-documents/${doc.doc_id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const filteredCostings = costings.filter(c =>
    !search || c.part_number?.toLowerCase().includes(search.toLowerCase()) ||
    c.part_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.supplier_part_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="p-12 text-center text-slate-400"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3"></div>Loading...</div>;
  if (!supplier) return <div className="p-12 text-center text-slate-400">Supplier not found</div>;

  return (
    <div className="space-y-5" data-testid="supplier-detail-page">
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => navigate('/suppliers')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} /> Suppliers</button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl">{supplier.name}</h2>
          <p className="text-slate-500 text-sm">{[supplier.location, supplier.payment_terms].filter(Boolean).join(' · ') || 'No details'}</p>
        </div>
        <div className="text-right">
          <p className="font-mono text-2xl font-black text-slate-900">{supplier.part_count || 0}</p>
          <p className="text-[10px] text-slate-400 uppercase">Active Costings</p>
        </div>
      </div>

      {/* Reverse lookup */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-2">
        <Search size={14} className="text-slate-400" />
        <input type="text" placeholder="Find by supplier's part number..." value={lookup} onChange={e => { setLookup(e.target.value); setLookupResults(null); }}
          onKeyDown={e => e.key === 'Enter' && handleLookup()}
          data-testid="supplier-part-lookup"
          className="border-0 outline-none text-sm flex-1 bg-transparent placeholder:text-slate-400" />
        <button onClick={handleLookup} className="px-3 h-7 text-xs font-semibold bg-slate-900 text-white rounded-sm hover:bg-slate-800">Lookup</button>
      </div>
      {lookupResults && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
          <p className="text-xs font-bold text-blue-700 mb-2">{lookupResults.length} match{lookupResults.length !== 1 ? 'es' : ''} found</p>
          {lookupResults.map((r, i) => (
            <div key={i} className="text-xs text-slate-700 py-1 border-b border-blue-100 last:border-0">
              Supplier: <span className="font-mono font-semibold">{r.supplier_part_number}</span> = Our: <span className="font-mono font-semibold">{r.our_part_number}</span> ({r.our_part_name}) — {r.customer_name} — PO: {fmtCur(r.po_price_per_unit)}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[{ key: 'costings', label: 'Parts & PO Prices', icon: Package }, { key: 'documents', label: 'Documents', icon: FileText }, { key: 'history', label: 'Price History', icon: Clock }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${tab === t.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Costings tab */}
      {tab === 'costings' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              <Search size={14} className="text-slate-400" />
              <input type="text" placeholder="Filter by part number..." value={search} onChange={e => setSearch(e.target.value)}
                className="border-0 outline-none text-sm flex-1 bg-transparent placeholder:text-slate-400" />
            </div>
            <button onClick={() => setShowAdd(true)} data-testid="add-costing-btn"
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 shadow-sm">
              <Plus size={14} /> Add Costing
            </button>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="overflow-auto max-h-[70vh]">
              <table className="data-table w-full min-w-[800px]">
                <thead><tr>
                  <th className="text-left">Supplier's Part #</th>
                  <th className="text-left">Our Part #</th>
                  <th className="text-left">Part Name</th>
                  <th className="text-left">Customer</th>
                  <th className="text-right">PO Price</th>
                  <th className="text-right">Customer Price</th>
                  <th className="text-right">Margin</th>
                  <th className="text-left">Effective</th>
                  <th className="text-center">Actions</th>
                </tr></thead>
                <tbody>
                  {filteredCostings.length === 0 ? (
                    <tr><td colSpan={9} className="py-12 text-center text-slate-400">No costings yet</td></tr>
                  ) : filteredCostings.map(c => {
                    const isPlating = c.source === 'plating';
                    const rowKey = c.costing_id || c.plating_costing_id;
                    const custPrice = Number(c.customer_selling_price) || 0;
                    const margin = custPrice - (c.po_price_per_unit || 0);
                    const marginPct = custPrice > 0 ? (margin / custPrice * 100) : 0;
                    return (
                      <tr key={rowKey} className="group">
                        <td className="font-mono text-sm text-slate-600">
                          {isPlating
                            ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-purple-50 text-purple-700 border border-purple-200"><Layers size={10} /> Plating</span>
                            : (c.supplier_part_number || <span className="text-slate-300">—</span>)}
                        </td>
                        <td className="font-mono font-semibold text-slate-800">{c.part_number}</td>
                        <td className="text-slate-700 text-sm max-w-[160px] truncate">{c.part_name}</td>
                        <td className="text-slate-600 text-xs">{c.customer_name}</td>
                        <td className="text-right font-mono text-sm font-semibold text-slate-800">{fmtCur(c.po_price_per_unit)}</td>
                        <td className="text-right font-mono text-sm text-slate-600">{custPrice > 0 ? fmtCur(custPrice) : '—'}</td>
                        <td className={`text-right font-mono text-sm font-semibold ${marginPct < 15 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {custPrice > 0 ? `${marginPct.toFixed(1)}%` : '—'}
                        </td>
                        <td className="text-xs text-slate-500">{fmtDate(c.effective_date)}</td>
                        <td className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {isPlating ? (
                              <button onClick={() => navigate('/plating')} title="Manage in Plating Suppliers"
                                data-testid={`goto-plating-${c.plating_costing_id}`}
                                className="p-1.5 text-purple-500 hover:bg-purple-50 rounded-sm"><Layers size={13} /></button>
                            ) : (
                              <>
                                <button onClick={() => setViewBreakdown(c)} title="View Breakdown"
                                  data-testid={`view-breakdown-${c.costing_id}`}
                                  className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm"><FileText size={13} /></button>
                                <button onClick={() => setEditCosting(c)} title="Edit" className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-sm"><Edit size={13} /></button>
                                <button onClick={() => handleDeleteCosting(c)} title="Deactivate" className="p-1.5 text-red-400 hover:bg-red-50 rounded-sm"><Trash2 size={13} /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Documents tab */}
      {tab === 'documents' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input type="file" ref={fileRef} onChange={handleUploadDoc} className="hidden" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-supplier-doc"
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50">
              <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
          {docs.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-8 text-center text-slate-400 text-sm">No documents yet</div>
          ) : (
            <div className="space-y-2">
              {docs.map(d => (
                <div key={d.doc_id} className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText size={16} className="text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{d.label || d.filename}</p>
                      <p className="text-xs text-slate-400">{fmtSize(d.size_bytes)} · {fmtDate(d.uploaded_at)} · {d.uploaded_by}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/supplier-documents/${d.doc_id}/download`}
                      className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-sm"><Download size={13} /></a>
                    <button onClick={() => handleDeleteDoc(d)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-sm"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <div className="space-y-3">
          {costings.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-8 text-center text-slate-400 text-sm">No costings with history</div>
          ) : costings.map(c => (
            <div key={c.costing_id || c.plating_costing_id} className="bg-white border border-slate-200 rounded-sm p-4">
              <p className="font-mono font-semibold text-sm text-slate-800 mb-2">{c.part_number} — {c.part_name}{c.source === 'plating' ? ' (Plating)' : c.supplier_part_number ? ` (Supplier: ${c.supplier_part_number})` : ''}</p>
              <div className="space-y-1">
                {(c.price_history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs py-1 border-b border-slate-50 last:border-0">
                    <span className="font-mono font-semibold text-slate-700 w-20">{fmtCur(h.price)}</span>
                    <span className="text-slate-400 w-24">{fmtDate(h.date)}</span>
                    <span className="text-slate-500 flex-1">{h.reason}</span>
                    <span className="text-slate-400">{h.updated_by}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <AddCostingModal supplierId={id} onClose={() => setShowAdd(false)} onSaved={fetchData} />}
      {editCosting && <AddCostingModal supplierId={id} editData={editCosting} onClose={() => setEditCosting(null)} onSaved={fetchData} />}
      {viewBreakdown && <SupplierCostBreakdownModal costing={viewBreakdown} onClose={() => setViewBreakdown(null)} />}
    </div>
  );
}
